import { useState, useCallback, useMemo, memo } from 'react';
import {
    View,
    AppNav,
} from '@smarterservices/smarter-ui';
import {
    ScreenReaderContent,
    IconHamburgerLine,
} from '@instructure/ui';
import { TabItem, CommunicationTab, IncidentReportTab, IdentificationTab } from './index';
import CustomTab from '../CustomTab';
import AudioConnection from '../Communication/AudioConnection';
import { useAppStore, useSettingsStore, getStore } from '../../../stores/StoreContext';

/**
 * AudioConnectionWithSettings - Wrapper component that connects AudioConnection to SettingsStore
 * 
 * Bridges the SettingsStore audio device preferences to the AudioConnection component.
 * Extracts device IDs from the stored device objects (which contain both deviceId and label)
 * and passes them to AudioConnection for actual device usage.
 * 
 * This wrapper enables the Audio Settings modal to control which devices are used
 * for communication audio in the SessionViewer.
 * 
 * Memoized to prevent unnecessary re-renders when rendered in the navigation bar.
 * 
 * @returns {JSX.Element} AudioConnection with device IDs from SettingsStore
 * @private
 */
const AudioConnectionWithSettings = memo(() => {
    const audioInputDevice = useSettingsStore((state) => state.audioInputDevice);
    const audioOutputDevice = useSettingsStore((state) => state.audioOutputDevice);
    const AppStore = getStore('appStore');

    // PR-3412: Pass explicit room to prevent context confusion
    const room = AppStore?.room;

    return (
        <AudioConnection
            room={room}
            audioInputDeviceId={audioInputDevice?.deviceId}
            audioOutputDeviceId={audioOutputDevice?.deviceId}
        />
    );
});

/**
 * NavBar - Session details navigation bar component
 * 
 * Renders the tabbed navigation interface for the SessionDetails panel. Manages tab visibility,
 * truncation behavior, and custom tab rendering. Supports dynamic tab configuration from
 * enabledComponents settings and handles incident report injection.
 * 
 * **Performance Optimization:**
 * This component is memoized to prevent unnecessary re-renders when SessionDetails re-renders
 * for unrelated state changes. Only re-renders when props actually change (selectedNavItem,
 * sessionDetailsConfig, incidentReport, etc.).
 * 
 * **Features:**
 * - Dynamic tab configuration via sessionDetailsConfig
 * - Custom tab rendering for special tabs (Communication, IncidentReport, Identity)
 * - Responsive truncation with "More" dropdown
 * - Audio connection controls in navigation bar
 * - Custom tab support with URL-based content
 * 
 * **Tab Types:**
 * - **Standard Tabs**: Use default TabItem component
 * - **Custom Rendered Tabs**: Use renderTab function for special behavior
 * - **URL Tabs**: Load external content via CustomTab component
 * 
 * @component
 * @param {Object} props - Component props
 * @param {Object} props.navOptions - Available navigation options/tabs configuration
 * @param {string} props.selectedNavItem - Currently selected tab key
 * @param {Function} props.onNavItemClick - Callback when tab is clicked
 * @param {Object} [props.sessionDetailsConfig] - Session details configuration from enabledComponents
 * @param {Array} [props.sessionDetailsConfig.tabs] - Array of tab IDs or tab objects to display
 * @param {Object} [props.incidentReport] - Incident report data (if present, adds incident report tab)
 * @param {number} [props.navigationHeight=40] - Height of navigation bar in pixels
 * 
 * @returns {JSX.Element} Navigation bar with tabs
 * 
 * @example
 * <NavBar
 *   navOptions={navOptions}
 *   selectedNavItem="flags"
 *   onNavItemClick={(key) => setSelectedTab(key)}
 *   sessionDetailsConfig={{ tabs: ['info', 'flags', 'journey'] }}
 *   incidentReport={incidentReportData}
 *   navigationHeight={40}
 * />
 */
function NavBarComponent({
    navOptions,
    selectedNavItem,
    onNavItemClick,
    sessionDetailsConfig,
    incidentReport,
    navigationHeight = 40,
}) {
    const [totalItemsCount, setTotalItemsCount] = useState(7);
    const [visibleItemsCount, setVisibleItemsCount] = useState(10); // Set high enough to show all tabs

    // Only re-render when room connection status changes
    const isRoomConnected = useAppStore((state) => state.isRoomConnected);

    /**
     * Memoize the update handler to prevent unnecessary re-renders
     * Called by AppNav when visible items count changes
     * Disabled to prevent truncation - we want all tabs visible
     */
    const handleUpdate = useCallback(({ visibleItemsCount }) => {
        // Don't update - keep visibleItemsCount at 10 to show all tabs
        // setVisibleItemsCount(visibleItemsCount);
    }, []);

    /**
     * Render truncate label for overflow menu
     * Shows count or hamburger icon based on visible items
     */
    const renderTruncateLabel = useCallback(() => {
        const hiddenItemsCount = totalItemsCount - visibleItemsCount;

        if (visibleItemsCount >= 2) {
            return `${hiddenItemsCount} More`;
        } else if (visibleItemsCount <= 0) {
            return 'Loading...';
        } else {
            return (
                <span>
                    <IconHamburgerLine size="small" inline={false} />
                    <ScreenReaderContent>{`${hiddenItemsCount} More`}</ScreenReaderContent>
                </span>
            );
        }
    }, [visibleItemsCount, totalItemsCount]);

    /**
     * Build navigation items based on configuration
     * Handles custom tabs, URL tabs, and incident report injection
     */
    const navItems = useMemo(() => {
        // Use configured tabs if provided, otherwise use defaults
        let itemsToDisplay;
        if (sessionDetailsConfig?.tabs) {
            // Map tabs configuration to item IDs
            itemsToDisplay = sessionDetailsConfig.tabs.map(tab =>
                typeof tab === 'string' ? tab : tab.id
            );
        } else {
            // Default tabs
            itemsToDisplay = ['info', 'flags'];
        }

        // Add incident report tab if there is an incident report
        if (incidentReport && !itemsToDisplay.includes('incidentReport')) {
            itemsToDisplay.push('incidentReport');
        } else if (!incidentReport) {
            // Remove incident report tab if it was in the config but there's no report
            itemsToDisplay = itemsToDisplay.filter(tab => tab !== 'incidentReport');
        }

        // Update total count for truncation logic
        setTotalItemsCount(itemsToDisplay.length);

        const items = itemsToDisplay.map((key) => {
            // Check if this is a custom tab from configuration
            const customTab = sessionDetailsConfig?.tabs?.find(
                tab => typeof tab === 'object' && tab.id === key
            );

            let option = navOptions[key];

            // If custom tab with URL, create a custom option using CustomTab component
            if (customTab && customTab.url) {
                option = {
                    label: customTab.title || key,
                    component: <CustomTab tab={customTab} />
                };
            } else if (customTab) {
                // Override label if custom title provided
                option = {
                    ...option,
                    label: customTab.title || option?.label || key
                };
            }

            if (!option) return null;

            // If option has a custom 'renderTab' function, use it to render the tab
            if (option.renderTab) {
                return option.renderTab(
                    key,
                    option,
                    selectedNavItem === key,
                    () => onNavItemClick(key)
                );
            }

            // Otherwise, use default TabItem rendering
            return (
                <TabItem
                    key={key}
                    tabKey={key}
                    option={option}
                    isSelected={selectedNavItem === key}
                    onClick={() => onNavItemClick(key)}
                />
            );
        });

        return items;
    }, [navOptions, selectedNavItem, sessionDetailsConfig, incidentReport, onNavItemClick]);

    return (
        <View as="div" background="primary-inverse">
            <AppNav
                screenReaderLabel="Session Details Navigation"
                visibleItemsCount={visibleItemsCount}
                background="primary-inverse"
                onUpdate={handleUpdate}
                themeOverride={{
                    height: `${navigationHeight}px`,
                }}
                renderTruncateLabel={renderTruncateLabel}
                renderAfterItems={
                    <>
                        {isRoomConnected && <AudioConnectionWithSettings />}
                    </>
                }
            >
                {navItems}
            </AppNav>
        </View>
    );
}

/**
 * Memoized export to prevent unnecessary re-renders from parent component
 * 
 * SessionDetails may re-render frequently due to state changes unrelated to the navigation bar.
 * Memoization ensures NavBar only re-renders when its props actually change:
 * - selectedNavItem (tab selection)
 * - sessionDetailsConfig (tab configuration)
 * - incidentReport (incident data)
 * - navOptions (tab definitions)
 * - onNavItemClick (callback - should be stable via useCallback)
 * 
 * This is safe because:
 * - All props are either primitives, stable callbacks, or memoized objects
 * - Internal state (isRoomConnected) comes from Zustand hook which triggers re-renders independently
 * - No risk of stale closures or missed updates
 */
const NavBar = memo(NavBarComponent);

export default NavBar;
